/**
 * AP-41 (5.2, 5.3) — Verträge beim Kunden: Karte je Dokument, Annehmen mit Passwort,
 * Kenntnisnahme, Widerspruch, Hinweisbalken und Sperrbildschirm.
 *
 * Liegt in `components/`, weil das Menü-Layout (Balken, Sperre) und die Einstellungsseite dieselben
 * Bausteine zeigen. Die Handlungen heißen nach ihrem Verb (Regel `speichern.md`, Fall 3).
 */
import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { FileSignature, FileText, LogOut, UserCheck } from 'lucide-react'
import { Modal } from '@symphenia/ui-components'
import { erfuelltRecht } from '@symphenia/shared-types'
import { Button } from '@/components/ui/button'
import { apiErrorText } from '@/lib/api-error'
import { useAuthStore } from '@/lib/auth.store'
import { fmtDateLong, fmtDateTime } from '@/lib/format-date'
import { fieldLabel, formCard, inputBase, selectBase, textareaBase } from '@/lib/form-styles'
import {
  oeffnePdf,
  useVertraege,
  vertraegeApi,
  type AnnahmeKandidat,
  type Annehmender,
  type VertragsDokument,
  type VertragsListe,
  type VertragsStand,
} from '@/lib/vertraege'

export const DARF_ANNEHMEN = 'core.contract.accept'
/** Mit dem Eintrag „Wer nimmt an?" wird eine Rolle vergeben — dafür braucht es auch dieses Recht. */
export const DARF_ROLLEN_VERGEBEN = 'core.role.manage'

/** Hat der Benutzer das Recht, die Verträge zu sehen? (Ob er annehmen darf: `useDarfHandeln`.) */
export function useDarfAnnehmen() {
  const rechte = useAuthStore((s) => s.user?.permissions)
  return erfuelltRecht(DARF_ANNEHMEN, rechte)
}

/**
 * Nachtrag 1 — darf dieser Benutzer annehmen, zur Kenntnis nehmen, widersprechen? Das Recht und,
 * wenn jemand als annehmende Person eingetragen ist, der Eintrag. Entschieden hat der Server.
 */
export function useDarfHandeln() {
  const recht = useDarfAnnehmen()
  const status = useVertraege((s) => s.status)
  return status ? status.darfAnnehmen : recht
}

/** „Anna Berger, Theo Tester" — Personen als Satzteil. */
export const namenText = (personen: Pick<Annehmender, 'name'>[]) => personen.map((p) => p.name).join(', ')

/**
 * Bei wem die Annahme liegt: die Eingetragenen, die arbeiten. Ein Eintrag bindet erst, wenn die
 * Person handeln kann — eine Eingeladene oder Deaktivierte hält niemanden vom Annehmen ab.
 */
export const bindende = (personen: Annehmender[] | undefined) => (personen ?? []).filter((p) => p.aktiv)
/** Eingetragen, aber noch nicht da: Die Einladung ist nicht angenommen. */
const eingeladene = (personen: Annehmender[] | undefined) => (personen ?? []).filter((p) => !p.aktiv && !p.angemeldetGewesen)

/** Stand neu laden — nach einer Handlung, damit Balken und Sperre sofort folgen. */
function useStandNeu() {
  const user = useAuthStore((s) => s.user)
  const laden = useVertraege((s) => s.laden)
  return useCallback(() => (user ? laden(`${user.tenantId}:${user.id}`, true) : Promise.resolve()), [user, laden])
}

const FARBE: Record<VertragsStand, string> = {
  ANGENOMMEN: 'border-green-500/30 bg-green-500/10 text-green-700 dark:text-green-400',
  OFFEN: 'border-amber-500/30 bg-amber-500/10 text-amber-700 dark:text-amber-400',
  UEBERFAELLIG: 'border-destructive/30 bg-destructive/10 text-destructive',
  MITTEILUNG: 'border-sky-500/30 bg-sky-500/10 text-sky-700 dark:text-sky-400',
  WIDERSPROCHEN: 'border-border bg-muted text-muted-foreground',
}

export function StandMarke({ stand, frist }: { stand: VertragsStand; frist: string | null }) {
  const { t } = useTranslation()
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium ${FARBE[stand]}`}>
      {t(`contracts.stand.${stand}`, { datum: frist ? fmtDateLong(frist) : '' })}
    </span>
  )
}

function AnnehmenDialog({ dok, companyName, onClose, onErledigt }: { dok: VertragsDokument; companyName: string; onClose: () => void; onErledigt: () => void }) {
  const { t } = useTranslation()
  const [role, setRole] = useState('')
  const [password, setPassword] = useState('')
  const [confirmed, setConfirmed] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const bereit = role.trim().length >= 2 && password.length > 0 && confirmed && !laeuft

  const annehmen = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!bereit) return
    setLaeuft(true)
    setFehler(null)
    try {
      await vertraegeApi.annehmen(dok.version.id, { role: role.trim(), password, confirmed })
      onErledigt()
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
      setPassword('')
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('contracts.annehmenTitel', { titel: dok.title })}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="vertrag-annehmen" disabled={!bereit}>
            {t('contracts.annehmen')}
          </Button>
        </div>
      }
    >
      <form id="vertrag-annehmen" onSubmit={annehmen} className="space-y-4 text-sm">
        <p className="text-muted-foreground">
          {dok.version.label} · {t('contracts.pruefsumme')}: <code className="text-xs">{dok.version.contentHash.slice(0, 16)}…</code>
        </p>
        <button type="button" onClick={() => void oeffnePdf(dok.version.id, dok.version.fileName)} className="inline-flex items-center gap-1.5 text-primary hover:opacity-80">
          <FileText size={16} /> {t('contracts.pdfAnsehen')}
        </button>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.funktion')}</span>
          <input className={inputBase} value={role} onChange={(e) => setRole(e.target.value)} placeholder={t('contracts.funktionBeispiel')} maxLength={120} autoFocus />
        </label>
        <label className="flex items-start gap-2">
          <input type="checkbox" className="mt-1" checked={confirmed} onChange={(e) => setConfirmed(e.target.checked)} />
          <span>{t('contracts.bestaetigung', { firma: companyName })}</span>
        </label>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.passwort')}</span>
          <input type="password" className={inputBase} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </label>
        {fehler && <p className="text-destructive">{fehler}</p>}
      </form>
    </Modal>
  )
}

function WiderspruchDialog({ dok, onClose, onErledigt }: { dok: VertragsDokument; onClose: () => void; onErledigt: () => void }) {
  const { t } = useTranslation()
  const [reason, setReason] = useState('')
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  const widersprechen = async () => {
    setLaeuft(true)
    setFehler(null)
    try {
      await vertraegeApi.widersprechen(dok.version.id, reason.trim())
      onErledigt()
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
    } finally {
      setLaeuft(false)
    }
  }
  return (
    <Modal
      isOpen
      onClose={onClose}
      title={t('contracts.widersprechenTitel', { titel: dok.title })}
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="button" variant="destructive" disabled={reason.trim().length < 3 || laeuft} onClick={() => void widersprechen()}>
            {t('contracts.widersprechen')}
          </Button>
        </div>
      }
    >
      <div className="space-y-3 text-sm">
        <p className="text-muted-foreground">{t('contracts.widersprechenHinweis')}</p>
        <label className="block">
          <span className={fieldLabel}>{t('contracts.begruendung')}</span>
          <textarea className={textareaBase} rows={4} value={reason} onChange={(e) => setReason(e.target.value)} maxLength={2000} />
        </label>
        {fehler && <p className="text-destructive">{fehler}</p>}
      </div>
    </Modal>
  )
}

/** Karte je Dokument: Stand, Fassung, „Was ist neu", PDF, Handlungen. */
export function VertragsKarte({ dok, companyName, onErledigt }: { dok: VertragsDokument; companyName: string; onErledigt: () => void }) {
  const { t } = useTranslation()
  const [dialog, setDialog] = useState<'annehmen' | 'widersprechen' | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  // Nachtrag 1: Liegt die Annahme bei jemand anderem, bleibt das PDF — die Handlungen nicht.
  const darf = useDarfHandeln()
  const annahmeDurch = bindende(useVertraege((s) => s.status?.annahmeDurch))
  const zuTun = dok.stand === 'OFFEN' || dok.stand === 'UEBERFAELLIG' || dok.stand === 'MITTEILUNG'
  const fertig = () => {
    setDialog(null)
    onErledigt()
  }
  const kenntnis = async () => {
    setFehler(null)
    try {
      await vertraegeApi.kenntnis(dok.version.id)
      onErledigt()
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
    }
  }
  return (
    <div className={formCard}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div>
          <h3 className="font-semibold text-foreground">{dok.title}</h3>
          <p className="text-xs text-muted-foreground">
            {dok.version.label}
            {dok.version.publishedAt && ` · ${t('contracts.veroeffentlicht', { datum: fmtDateLong(dok.version.publishedAt) })}`}
          </p>
        </div>
        <StandMarke stand={dok.stand} frist={dok.frist} />
      </div>
      {dok.version.changeSummary && dok.stand !== 'ANGENOMMEN' && (
        <p className="mt-3 whitespace-pre-line text-sm text-foreground">
          <span className="font-medium">{t('contracts.wasIstNeu')}: </span>
          {dok.version.changeSummary}
        </p>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" size="sm" onClick={() => void oeffnePdf(dok.version.id, dok.version.fileName)}>
          <FileText size={14} className="mr-1.5" /> {t('contracts.pdfAnsehen')}
        </Button>
        {darf && (dok.stand === 'OFFEN' || dok.stand === 'UEBERFAELLIG') && (
          <Button type="button" size="sm" onClick={() => setDialog('annehmen')}>
            {t('contracts.annehmen')}
          </Button>
        )}
        {darf && dok.stand === 'MITTEILUNG' && (
          <>
            <Button type="button" size="sm" onClick={() => void kenntnis()}>
              {t('contracts.kenntnis')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => setDialog('widersprechen')}>
              {t('contracts.widersprechen')}
            </Button>
          </>
        )}
        {!darf && zuTun && annahmeDurch.length > 0 && (
          <span className="text-sm text-muted-foreground">{t('contracts.annahmeNurDurch', { namen: namenText(annahmeDurch) })}</span>
        )}
      </div>
      {fehler && <p className="mt-2 text-sm text-destructive">{fehler}</p>}
      {dialog === 'annehmen' && <AnnehmenDialog dok={dok} companyName={companyName} onClose={() => setDialog(null)} onErledigt={fertig} />}
      {dialog === 'widersprechen' && <WiderspruchDialog dok={dok} onClose={() => setDialog(null)} onErledigt={fertig} />}
    </div>
  )
}

/** Liste der Dokumente samt Nachladen — für die Einstellungsseite und den Sperrbildschirm. */
export function useVertragsListe() {
  const { t } = useTranslation()
  const standNeu = useStandNeu()
  const [liste, setListe] = useState<VertragsListe | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const laden = useCallback(async () => {
    try {
      setListe(await vertraegeApi.liste())
      setFehler(null)
    } catch (err) {
      setFehler(apiErrorText(err, t('common.error')))
    }
  }, [t])
  useEffect(() => {
    void laden()
  }, [laden])
  const erledigt = useCallback(async () => {
    await Promise.all([laden(), standNeu()])
  }, [laden, standNeu])
  return { liste, fehler, erledigt }
}

/**
 * Balken über dem Inhalt, solange etwas offen ist — für die, die das Recht haben.
 *
 * Nachtrag 1: Wer das Recht hat, aber nicht eingetragen ist, liest, bei wem die Annahme liegt. Die
 * Person des Betreibers arbeitet in einem gesperrten Betrieb weiter (für alle anderen steht dort der
 * Sperrbildschirm) und liest hier, dass die anderen warten — und auf wen.
 */
export function VertragsHinweis() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const recht = useDarfAnnehmen()
  const status = useVertraege((s) => s.status)
  if (!recht || !status || status.gesperrt) return null
  const bei = namenText(bindende(status.annahmeDurch))
  let text: string
  if (status.mandantGesperrt) {
    const eingeladen = namenText(eingeladene(status.annahmeDurch))
    text = bei
      ? t('contracts.hinweisBetreiber', { firma: status.companyName, namen: bei })
      : eingeladen
        ? t('contracts.hinweisBetreiberEingeladen', { firma: status.companyName, namen: eingeladen })
        : t('contracts.hinweisBetreiberOhne', { firma: status.companyName })
  } else {
    const offen = status.offen.filter((o) => o.stand === 'OFFEN' || o.stand === 'MITTEILUNG')
    if (!offen.length) return null
    const erste = offen[0]
    text = !status.darfAnnehmen
      ? t('contracts.hinweisDurchAndere', { titel: erste.title, namen: bei })
      : erste.stand === 'MITTEILUNG'
        ? t('contracts.hinweisMitteilung', { titel: erste.title })
        : erste.frist
          ? t('contracts.hinweisFrist', { titel: erste.title, datum: fmtDateLong(erste.frist) })
          : t('contracts.hinweisOhneFrist', { titel: erste.title })
    if (offen.length > 1) text += ` ${t('contracts.hinweisWeitere', { anzahl: offen.length - 1 })}`
  }
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-md border border-amber-500/30 bg-amber-500/10 px-4 py-3 text-sm text-foreground">
      <FileSignature size={18} className="shrink-0 text-amber-600 dark:text-amber-400" />
      <span className="flex-1">{text}</span>
      <button
        type="button"
        onClick={() => navigate('/settings/vertraege')}
        className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {t('contracts.oeffnen')}
      </button>
    </div>
  )
}

/** Hinter dem Namen: Die Person ist eingetragen, aber (noch) nicht da — ihr Eintrag bindet nicht. */
function PersonenMarke({ person }: { person: Annehmender }) {
  const { t } = useTranslation()
  if (person.aktiv) return null
  return (
    <span className="rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-muted-foreground">
      {t(person.angemeldetGewesen ? 'contracts.personDeaktiviert' : 'contracts.personEingeladen')}
    </span>
  )
}

/**
 * Nachtrag 1 — „Wer nimmt an?": die Eingetragenen, Eintragen und Austragen.
 *
 * Eine Handlung (Regel `speichern.md`, Fall 3): Mit dem Eintrag bekommt die Person die Rolle
 * „Vertragsannahme" und eine Nachricht — darum zwei Knöpfe mit ihrem Verb und kein Feld, das beim
 * Verlassen speichert. Ändern darf, wer Verträge annehmen **und** Rollen vergeben darf; alle
 * anderen lesen die Liste. `nurZumAendern`: auf dem Sperrbildschirm, wo die Liste sonst nur
 * wiederholte, was der Satz darüber schon sagt.
 */
export function AnnahmeDurch({ nurZumAendern = false }: { nurZumAendern?: boolean }) {
  const { t } = useTranslation()
  const rechte = useAuthStore((s) => s.user?.permissions)
  const darfAendern = erfuelltRecht(DARF_ANNEHMEN, rechte) && erfuelltRecht(DARF_ROLLEN_VERGEBEN, rechte)
  const firma = useVertraege((s) => s.status?.companyName) ?? ''
  const standNeu = useStandNeu()
  const [personen, setPersonen] = useState<AnnahmeKandidat[] | null>(null)
  const [wahl, setWahl] = useState('')
  // Der Fehler selbst, nicht sein Text: Der Satz entsteht erst beim Anzeigen.
  const [fehler, setFehler] = useState<unknown>(null)
  const [laeuft, setLaeuft] = useState(false)
  const zeigen = darfAendern || !nurZumAendern

  useEffect(() => {
    if (!zeigen) return
    let aktuell = true
    vertraegeApi
      .annehmende()
      .then((p) => aktuell && setPersonen(p))
      .catch((err: unknown) => aktuell && setFehler(err))
    return () => {
      aktuell = false
    }
  }, [zeigen])

  if (!zeigen) return null
  const eingetragen = personen?.filter((p) => p.eingetragen) ?? []
  const waehlbar = personen?.filter((p) => !p.eingetragen) ?? []

  const setze = async (userIds: string[]) => {
    setLaeuft(true)
    setFehler(null)
    try {
      setPersonen(await vertraegeApi.setzeAnnehmende(userIds))
      setWahl('')
      // Balken, Sperrbildschirm und die Knöpfe an den Karten folgen dem Stand des Servers.
      await standNeu()
    } catch (err) {
      setFehler(err)
    } finally {
      setLaeuft(false)
    }
  }

  return (
    <div className={formCard}>
      <h3 className="flex items-center gap-2 font-semibold text-foreground">
        <UserCheck size={18} /> {t('contracts.werNimmtAn')}
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">{t('contracts.werNimmtAnText', { firma })}</p>
      {personen && eingetragen.length === 0 && <p className="mt-3 text-sm text-foreground">{t('contracts.niemandEingetragen')}</p>}
      {eingetragen.length > 0 && (
        <ul className="mt-3 divide-y divide-border/60">
          {eingetragen.map((p) => (
            <li key={p.userId} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2 text-sm">
              <span className="font-medium text-foreground">{p.name}</span>
              {p.email && p.email !== p.name && <span className="text-muted-foreground">{p.email}</span>}
              <PersonenMarke person={p} />
              {darfAendern && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="ml-auto"
                  disabled={laeuft}
                  onClick={() => void setze(eingetragen.filter((e) => e.userId !== p.userId).map((e) => e.userId))}
                >
                  {t('contracts.austragen')}
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
      {darfAendern ? (
        <div className="mt-4 flex flex-wrap items-end gap-2">
          <label className="min-w-[14rem] flex-1">
            <span className={fieldLabel}>{t('contracts.person')}</span>
            <select className={selectBase} value={wahl} onChange={(e) => setWahl(e.target.value)} disabled={laeuft || waehlbar.length === 0}>
              <option value="">{t('contracts.personWaehlen')}</option>
              {waehlbar.map((p) => (
                <option key={p.userId} value={p.userId}>
                  {p.name}
                  {p.email && p.email !== p.name ? ` · ${p.email}` : ''}
                  {p.aktiv ? '' : ` (${t('contracts.eingeladen')})`}
                </option>
              ))}
            </select>
          </label>
          <Button type="button" disabled={!wahl || laeuft} onClick={() => void setze([...eingetragen.map((e) => e.userId), wahl])}>
            {t('contracts.eintragen')}
          </Button>
        </div>
      ) : (
        <p className="mt-3 text-xs text-muted-foreground">{t('contracts.nurMitRollenrecht')}</p>
      )}
      {fehler != null && <p className="mt-2 text-sm text-destructive">{apiErrorText(fehler, t('common.error'))}</p>}
    </div>
  )
}

function SperreMitRecht({ companyName }: { companyName: string }) {
  const { t } = useTranslation()
  const { liste, fehler, erledigt } = useVertragsListe()
  const darf = useDarfHandeln()
  const bei = namenText(bindende(useVertraege((s) => s.status?.annahmeDurch)))
  const offen = liste?.dokumente.filter((d) => d.stand !== 'ANGENOMMEN' && d.stand !== 'WIDERSPROCHEN') ?? []
  return (
    <>
      <p className="text-sm text-muted-foreground">
        {darf ? t('contracts.sperreMitRecht', { firma: companyName }) : t('contracts.sperreDurchAndere', { firma: companyName, namen: bei })}
      </p>
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
      {/* Wer Rollen vergeben darf, ändert auch hier, wer annimmt — sonst hinge ein gesperrter Betrieb an einer Person, die nicht mehr da ist. */}
      <AnnahmeDurch nurZumAendern />
      <div className="space-y-4">
        {offen.map((d) => (
          <VertragsKarte key={d.documentId} dok={d} companyName={companyName} onErledigt={() => void erledigt()} />
        ))}
      </div>
    </>
  )
}

/** Statt der Seite, wenn der Betrieb eine Frist versäumt hat (5.3). Abmelden bleibt möglich. */
export function VertragsSperre() {
  const { t } = useTranslation()
  const recht = useDarfAnnehmen()
  const status = useVertraege((s) => s.status)
  const logout = useAuthStore((s) => s.logout)
  if (!status?.gesperrt) return null
  const bei = namenText(bindende(status.annahmeDurch))
  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 sm:p-6">
      <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
        <FileSignature size={22} /> {t('contracts.sperreTitel')}
      </h1>
      {recht ? (
        <SperreMitRecht companyName={status.companyName} />
      ) : (
        <p className="text-sm text-foreground">{bei ? t('contracts.sperreOhneRechtDurch', { namen: bei }) : t('contracts.sperreOhneRecht')}</p>
      )}
      <Button type="button" variant="outline" onClick={() => void logout()}>
        <LogOut size={16} className="mr-1.5" /> {t('nav.logout')}
      </Button>
    </div>
  )
}

/** Verlauf aller Zustimmungen — mit PDF der jeweiligen Fassung. */
export function VertragsVerlauf({ liste }: { liste: VertragsListe }) {
  const { t } = useTranslation()
  if (!liste.verlauf.length) return <p className="text-sm text-muted-foreground">{t('contracts.verlaufLeer')}</p>
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left text-xs text-muted-foreground">
            <th className="py-2 pr-3">{t('contracts.dokument')}</th>
            <th className="py-2 pr-3">{t('contracts.fassung')}</th>
            <th className="py-2 pr-3">{t('contracts.handlung')}</th>
            <th className="py-2 pr-3">{t('contracts.person')}</th>
            <th className="py-2 pr-3">{t('contracts.zeitpunkt')}</th>
            <th className="py-2" />
          </tr>
        </thead>
        <tbody>
          {liste.verlauf.map((z) => (
            <tr key={z.id} className="border-b border-border/60 align-top">
              <td className="py-2 pr-3 text-foreground">{z.document.title}</td>
              <td className="py-2 pr-3">{z.version.label}</td>
              <td className="py-2 pr-3">
                {t(`contracts.art.${z.kind}`)}
                {z.reason && <div className="text-xs text-muted-foreground">{z.reason}</div>}
              </td>
              <td className="py-2 pr-3">
                {z.personName}
                {z.personRole && <div className="text-xs text-muted-foreground">{z.personRole}</div>}
              </td>
              <td className="py-2 pr-3 whitespace-nowrap">{fmtDateTime(z.createdAt)}</td>
              <td className="py-2 text-right">
                <button type="button" className="text-primary hover:opacity-80" onClick={() => void oeffnePdf(z.version.id, z.version.fileName)} title={t('contracts.pdfAnsehen')}>
                  <FileText size={16} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
